/**
 * @file     visitorProfileService.ts
 * @readme   ./README.md
 * @purpose  Pure functions for reading, writing, and managing the visitor profile in localStorage.
 */

import type {
  DataPointEntry,
  DataPointInput,
  DataPointValue,
  VisitorProfile,
} from './types';

const STORAGE_KEY = 'visitor-profile:v1';
const TTL_DAYS = 30;

function isSSR(): boolean {
  return typeof window === 'undefined';
}

function createProfile(): VisitorProfile {
  const now = new Date().toISOString();
  const expires = new Date(Date.now() + TTL_DAYS * 24 * 60 * 60 * 1000).toISOString();
  return {
    version: 1,
    createdAt: now,
    updatedAt: now,
    expiresAt: expires,
    dataPoints: {},
  };
}

function loadRaw(): VisitorProfile | null {
  if (isSSR()) return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as VisitorProfile;
    if (parsed.version !== 1) {
      clearProfile();
      return null;
    }
    return parsed;
  } catch {
    clearProfile();
    return null;
  }
}

function save(profile: VisitorProfile): void {
  if (isSSR()) return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(profile));
  } catch {
    // Quota exceeded or private browsing, silent no-op
  }
}

export function readProfile(): VisitorProfile | null {
  const profile = loadRaw();
  if (!profile) return null;
  if (new Date(profile.expiresAt) < new Date()) {
    clearProfile();
    return null;
  }
  return profile;
}

export function writeDataPoint(key: string, value: DataPointValue, source: string): void {
  if (isSSR()) return;
  const profile = readProfile() ?? createProfile();
  const now = new Date().toISOString();
  profile.dataPoints[key] = { value, source, collectedAt: now };
  profile.updatedAt = now;
  save(profile);
}

export function writeDataPoints(points: DataPointInput[]): void {
  if (isSSR()) return;
  const profile = readProfile() ?? createProfile();
  const now = new Date().toISOString();
  for (const point of points) {
    profile.dataPoints[point.key] = {
      value: point.value,
      source: point.source,
      collectedAt: now,
    };
  }
  profile.updatedAt = now;
  save(profile);
}

export function getDataPoint(key: string): DataPointEntry | null {
  const profile = readProfile();
  if (!profile) return null;
  return profile.dataPoints[key] ?? null;
}

export function hasDataPoint(key: string): boolean {
  const profile = readProfile();
  if (!profile) return false;
  return key in profile.dataPoints;
}

export function clearProfile(): void {
  if (isSSR()) return;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Silent no-op
  }
}

export function getProfileSnapshot(): Record<string, DataPointValue> {
  const profile = readProfile();
  if (!profile) return {};
  const snapshot: Record<string, DataPointValue> = {};
  for (const [key, entry] of Object.entries(profile.dataPoints)) {
    snapshot[key] = entry.value;
  }
  return snapshot;
}

export function getProfileAge(): number | null {
  const profile = readProfile();
  if (!profile) return null;
  const created = new Date(profile.createdAt).getTime();
  const now = Date.now();
  return (now - created) / (1000 * 60 * 60 * 24);
}
